import { useState, useEffect, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Search, X, Plus, Database, Loader2, Clock, Pencil, Wifi, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { buildEdgeFunctionHeaders } from "@/lib/session";
import { supabase } from "@/integrations/supabase/client";
import ConnectFormRenderer, { type ConnectFormField, buildFormValues, extractConnectionParams } from "./ConnectFormRenderer";

interface Connector {
  id: string;
  name: string;
  description: string;
  color: string;
  initials: string;
  logo: string | null;
  available: boolean;
  db_type: string | null;
  category: string;
  display_order: number;
  connect_form: ConnectFormField[] | null;
  allow_remote: boolean;
}

interface Category {
  name: string;
  connectors: Connector[];
}

interface SourceConnector {
  id: string;
  name: string;
  db_type: string;
  enabled: boolean;
  created_at: string;
  connection_params: Record<string, unknown> | null;
  connect_type: string;
}

// Build a set of all existing sidebar connector IDs for "available" detection
const existingSidebarIds = new Set([
  "ds-manual-uploads", "ds-gmail", "ds-google-drive", "ds-scripts", "ds-mcp-servers", "ds-sql-db",
  "ds-rest-apis", "ds-webhooks", "ds-frontend-sdks",
  "telegram", "whatsapp", "slack", "google-chat", "ms-teams",
  "sl-facebook", "sl-instagram", "sl-google-reviews", "sl-playstore", "sl-appstore",
  "oc-freshdesk", "oc-zendesk", "oc-salesforce", "oc-zoho", "oc-shopify",
  "mk-webengage", "mk-moengage", "mk-clevertap",
]);

const ConnectorLogo = ({ connector }: { connector: Connector }) => {
  const [imgError, setImgError] = useState(false);

  if (connector.logo && !imgError) {
    return (
      <div
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg overflow-hidden"
        style={{ backgroundColor: connector.color }}
      >
        <img
          src={`https://cdn.simpleicons.org/${connector.logo}/white`}
          alt={connector.name}
          className="h-5.5 w-5.5 object-contain"
          style={{ width: 22, height: 22 }}
          onError={() => setImgError(true)}
          loading="lazy"
        />
      </div>
    );
  }

  return (
    <div
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-white text-xs font-bold"
      style={{ backgroundColor: connector.color }}
    >
      {connector.initials}
    </div>
  );
};

interface AllConnectorsProps {
  onSelectConnector: (id: string) => void;
}

const RECENT_CONNECTORS_KEY = "recent_connectors";
const MAX_RECENT = 6;

const getRecentConnectorIds = (): string[] => {
  try {
    const raw = localStorage.getItem(RECENT_CONNECTORS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const saveRecentConnectorId = (id: string) => {
  const ids = getRecentConnectorIds().filter((i) => i !== id);
  ids.unshift(id);
  localStorage.setItem(RECENT_CONNECTORS_KEY, JSON.stringify(ids.slice(0, MAX_RECENT)));
};

const AllConnectors = ({ onSelectConnector }: AllConnectorsProps) => {
  const { profile, markHasData } = useAuth();
  const [search, setSearch] = useState("");
  const [requestOpen, setRequestOpen] = useState(false);
  const [connectorName, setConnectorName] = useState("");
  const [purpose, setPurpose] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [recentIds, setRecentIds] = useState<string[]>(getRecentConnectorIds);

  // Connector catalog from DB
  const [allConnectors, setAllConnectors] = useState<Connector[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);

  // Datasource db_types from pipeline_source_connector
  const [datasourceDbTypes, setDatasourceDbTypes] = useState<string[]>([]);

  // Connector type popup state
  const [selectedConnectorType, setSelectedConnectorType] = useState<Connector | null>(null);
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [existingConnectors, setExistingConnectors] = useState<SourceConnector[]>([]);
  const [existingLoading, setExistingLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});
  const [addingSaving, setAddingSaving] = useState(false);

  // Edit existing connector state
  const [editingConnectorId, setEditingConnectorId] = useState<string | null>(null);
  const [editFormValues, setEditFormValues] = useState<Record<string, unknown>>({});
  const [editSaving, setEditSaving] = useState(false);

  // Connect mode: 'sync' or 'remote' — shown when allow_remote is true
  const [connectMode, setConnectMode] = useState<"sync" | "remote">("sync");

  // Fetch connector catalog from database
  useEffect(() => {
    const fetchCatalog = async () => {
      setCatalogLoading(true);
      const { data, error } = await supabase
        .from("all_connectors")
        .select("*")
        .order("display_order", { ascending: true });
      if (error) {
        toast.error("Failed to load connectors catalog");
      } else {
        const dbConnectors = (data as unknown as Connector[]) || [];
        // Ensure Manual Uploads and Scripts always appear in the catalog
        const builtInIds = new Set(["ds-manual-uploads", "ds-scripts"]);
        const filtered = dbConnectors.filter((c) => !builtInIds.has(c.id));
        const builtIn: Connector[] = [
          {
            id: "ds-manual-uploads",
            name: "Manual Uploads",
            description: "Upload files manually to ingest data",
            color: "#6366f1",
            initials: "MU",
            logo: null,
            available: true,
            db_type: null,
            category: "Developer Tools",
            display_order: -2,
            connect_form: null,
            allow_remote: false,
          },
          {
            id: "ds-scripts",
            name: "Scripts",
            description: "Run custom scripts to pull data from any source",
            color: "#0ea5e9",
            initials: "SC",
            logo: null,
            available: true,
            db_type: null,
            category: "Developer Tools",
            display_order: -1,
            connect_form: null,
            allow_remote: false,
          },
        ];
        setAllConnectors([...builtIn, ...filtered]);
      }
      setCatalogLoading(false);
    };
    fetchCatalog();
  }, []);

  // Fetch distinct db_types that have pipeline_source_connector entries
  useEffect(() => {
    if (!profile?.client_id) return;
    const fetchDatasourceTypes = async () => {
      const { data } = await supabase
        .from("pipeline_source_connector")
        .select("db_type")
        .eq("client_id", profile.client_id);
      if (data) {
        const unique = [...new Set((data as { db_type: string }[]).map((d) => d.db_type))];
        setDatasourceDbTypes(unique);
      }
    };
    fetchDatasourceTypes();
  }, [profile?.client_id]);

  // Group connectors by category
  const categories = useMemo(() => {
    const grouped: Record<string, Connector[]> = {};
    const categoryOrder: string[] = [];
    allConnectors.forEach((c) => {
      if (!grouped[c.category]) {
        grouped[c.category] = [];
        categoryOrder.push(c.category);
      }
      grouped[c.category].push(c);
    });
    return categoryOrder.map((name) => ({ name, connectors: grouped[name] }));
  }, [allConnectors]);

  const handleRequestSubmit = async () => {
    if (!connectorName.trim() || !purpose.trim()) return;
    setSubmitting(true);
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const headers = await buildEdgeFunctionHeaders();
      const res = await fetch(`${supabaseUrl}/functions/v1/request-connector`, {
        method: "POST",
        headers,
        body: JSON.stringify({ connector_name: connectorName.trim(), purpose: purpose.trim() }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to send request");
      }
      toast.success("We have received your request. We will evaluate and get back to you.");
      setRequestOpen(false);
      setConnectorName("");
      setPurpose("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const openConnectorTypeDialog = async (connector: Connector) => {
    setSelectedConnectorType(connector);
    setTypeDialogOpen(true);
    setShowAddForm(false);
    setEditingConnectorId(null);
    setEditFormValues({});
    setConnectMode("sync");
    setFormValues(connector.connect_form ? buildFormValues(connector.connect_form) : {});

    if (!connector.db_type || !profile?.client_id) {
      setExistingConnectors([]);
      return;
    }

    setExistingLoading(true);
    const { data } = await supabase
      .from("pipeline_source_connector")
      .select("id, name, db_type, enabled, created_at, connection_params, connect_type")
      .eq("client_id", profile.client_id)
      .eq("db_type", connector.db_type as any)
      .order("created_at", { ascending: false });
    setExistingConnectors((data as unknown as SourceConnector[]) || []);
    setExistingLoading(false);
  };

  const handleAddConnector = async () => {
    const connName = (formValues.name as string || "").trim();
    if (!selectedConnectorType?.db_type || !profile?.client_id || !connName) {
      toast.error("Connection name is required");
      return;
    }
    setAddingSaving(true);
    const connectionParams = selectedConnectorType.connect_form
      ? extractConnectionParams(selectedConnectorType.connect_form, formValues)
      : {};

    const isRemote = connectMode === "remote" && selectedConnectorType.allow_remote;

    const { error } = await supabase.from("pipeline_source_connector").insert({
      client_id: profile.client_id,
      name: connName,
      db_type: selectedConnectorType.db_type,
      connection_params: connectionParams,
      enabled: false,
      logo: selectedConnectorType.logo || null,
      logo_color: selectedConnectorType.color || null,
      connect_type: isRemote ? "remote" : "sync",
    } as any);

    if (error) {
      toast.error(error.message);
    } else {
      // If remote mode, also create a dummy MCP server entry
      if (isRemote) {
        const mcpUrl = (connectionParams.host as string)
          ? `${(connectionParams.ssl_enabled ? "https" : "http")}://${connectionParams.host}:${connectionParams.port || 8123}`
          : (connectionParams.connection_string as string) || "";
        await supabase.from("data_sources").insert({
          user_id: profile.id,
          client_id: profile.client_id,
          profile_id: profile.id,
          name: `${connName} (Remote MCP)`,
          description: `Remote MCP connection for ${selectedConnectorType.name} - ${connName}`,
          url: mcpUrl || "https://placeholder-mcp-url",
          auth_headers: {},
          enabled: false,
          internal: true,
        } as any);
        toast.success("Remote connector added with MCP server entry");
      } else {
        toast.success("Connector added (disabled by default)");
      }
      markHasData();
      setShowAddForm(false);
      setFormValues({});
      // Refresh the list
      const { data } = await supabase
        .from("pipeline_source_connector")
        .select("id, name, db_type, enabled, created_at, connection_params, connect_type")
        .eq("client_id", profile.client_id)
        .eq("db_type", selectedConnectorType.db_type as any)
        .order("created_at", { ascending: false });
      setExistingConnectors((data as unknown as SourceConnector[]) || []);
    }
    setAddingSaving(false);
  };

  const startEditConnector = (conn: SourceConnector) => {
    setShowAddForm(false);
    setEditingConnectorId(conn.id);
    if (selectedConnectorType?.connect_form) {
      setEditFormValues(
        buildFormValues(
          selectedConnectorType.connect_form,
          (conn.connection_params || {}) as Record<string, unknown>,
          conn.name,
        ),
      );
    } else {
      setEditFormValues({ name: conn.name });
    }
  };

  const cancelEdit = () => {
    setEditingConnectorId(null);
    setEditFormValues({});
  };

  const handleSaveEdit = async () => {
    if (!selectedConnectorType || !editingConnectorId) return;
    const connName = ((editFormValues.name as string) || "").trim();
    if (!connName) {
      toast.error("Connection name is required");
      return;
    }
    setEditSaving(true);

    const connectionParams = selectedConnectorType.connect_form
      ? extractConnectionParams(selectedConnectorType.connect_form, editFormValues)
      : {};

    // Preserve existing password if not provided
    const existingConn = existingConnectors.find((c) => c.id === editingConnectorId);
    if (selectedConnectorType.connect_form && existingConn?.connection_params) {
      for (const field of selectedConnectorType.connect_form) {
        if (field.type === "password" && !editFormValues[field.name] && (existingConn.connection_params as Record<string, unknown>)[field.name]) {
          connectionParams[field.name] = (existingConn.connection_params as Record<string, unknown>)[field.name];
        }
      }
    }

    const { error } = await supabase
      .from("pipeline_source_connector")
      .update({
        name: connName,
        connection_params: connectionParams,
      } as any)
      .eq("id", editingConnectorId);

    if (error) {
      toast.error(error.message);
    } else {
      toast.success("Connection updated");
      setEditingConnectorId(null);
      setEditFormValues({});
      // Refresh the list
      if (profile?.client_id && selectedConnectorType.db_type) {
        const { data } = await supabase
          .from("pipeline_source_connector")
          .select("id, name, db_type, enabled, created_at, connection_params, connect_type")
          .eq("client_id", profile.client_id)
          .eq("db_type", selectedConnectorType.db_type as any)
          .order("created_at", { ascending: false });
        setExistingConnectors((data as unknown as SourceConnector[]) || []);
      }
    }
    setEditSaving(false);
  };

  // Recently opened connectors (ordered by recency) merged with datasource connectors
  const recentConnectors = useMemo(() => {
    if (allConnectors.length === 0) return [];
    const byId = new Map(allConnectors.map((c) => [c.id, c]));

    // Start with localStorage recent connectors
    const result: Connector[] = [];
    const seenIds = new Set<string>();

    for (const id of recentIds) {
      const c = byId.get(id);
      if (c) {
        result.push(c);
        seenIds.add(c.id);
      }
    }

    // Add connectors that have datasources in pipeline_source_connector (by db_type)
    if (datasourceDbTypes.length > 0) {
      const dbTypeSet = new Set(datasourceDbTypes);
      for (const c of allConnectors) {
        if (c.db_type && dbTypeSet.has(c.db_type) && !seenIds.has(c.id)) {
          result.push(c);
          seenIds.add(c.id);
        }
      }
    }

    return result;
  }, [recentIds, allConnectors, datasourceDbTypes]);

  const filteredCategories = categories
    .map((cat) => ({
      ...cat,
      connectors: cat.connectors.filter(
        (c) =>
          c.name.toLowerCase().includes(search.toLowerCase()) ||
          c.description.toLowerCase().includes(search.toLowerCase())
      ),
    }))
    .filter((cat) => cat.connectors.length > 0);

  const totalConnectors = allConnectors.length;

  return (
    <div className="flex flex-1 flex-col overflow-auto">
      <div className="border-b border-border px-6 py-5">
        <h1 className="text-2xl font-semibold">All Connectors</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Browse {totalConnectors}+ connectors across {categories.length} categories. Connect your tools and data sources.
        </p>
        <div className="flex items-center gap-3 mt-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search connectors..."
              className="pl-9 pr-9"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <Button onClick={() => setRequestOpen(true)} size="sm">
            <Plus className="h-4 w-4 mr-1.5" />
            Request a Connector
          </Button>
        </div>
      </div>

      <div className="px-6 py-5 space-y-8">
        {catalogLoading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground gap-2 text-sm">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading connectors...
          </div>
        ) : (
          <>
            {!search && recentConnectors.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <h2 className="text-lg font-semibold">Recently Opened</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {recentConnectors.map((connector) => {
                    const isAvailable = existingSidebarIds.has(connector.id);
                    const hasDbType = !!connector.db_type;
                    const isClickable = isAvailable || hasDbType;
                    return (
                      <button
                        key={connector.id}
                        onClick={() => {
                          if (isAvailable) {
                            saveRecentConnectorId(connector.id);
                            setRecentIds(getRecentConnectorIds());
                            onSelectConnector(connector.id);
                          } else if (hasDbType) {
                            saveRecentConnectorId(connector.id);
                            setRecentIds(getRecentConnectorIds());
                            openConnectorTypeDialog(connector);
                          }
                        }}
                        className={`flex items-start gap-3 rounded-lg border border-border p-3 text-left transition-colors ${
                          isClickable
                            ? "hover:bg-accent hover:border-primary/30 cursor-pointer"
                            : "opacity-75 cursor-default"
                        }`}
                      >
                        <ConnectorLogo connector={connector} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium leading-tight">{connector.name}</span>
                            {isAvailable && (
                              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 shrink-0">
                                Available
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                            {connector.description}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {filteredCategories.map((category) => (
              <div key={category.name}>
                <h2 className="text-lg font-semibold mb-3">{category.name}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {category.connectors.map((connector) => {
                    const isAvailable = existingSidebarIds.has(connector.id);
                    const hasDbType = !!connector.db_type;
                    const isClickable = isAvailable || hasDbType;
                    return (
                      <button
                        key={connector.id}
                        onClick={() => {
                          if (isAvailable) {
                            saveRecentConnectorId(connector.id);
                            setRecentIds(getRecentConnectorIds());
                            onSelectConnector(connector.id);
                          } else if (hasDbType) {
                            saveRecentConnectorId(connector.id);
                            setRecentIds(getRecentConnectorIds());
                            openConnectorTypeDialog(connector);
                          }
                        }}
                        className={`flex items-start gap-3 rounded-lg border border-border p-3 text-left transition-colors ${
                          isClickable
                            ? "hover:bg-accent hover:border-primary/30 cursor-pointer"
                            : "opacity-75 cursor-default"
                        }`}
                      >
                        <ConnectorLogo connector={connector} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium leading-tight">{connector.name}</span>
                            {isAvailable && (
                              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 shrink-0">
                                Available
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                            {connector.description}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            {filteredCategories.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12">
                <p className="text-muted-foreground text-sm">No connectors match your search.</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Request Connector Dialog */}
      <Dialog open={requestOpen} onOpenChange={setRequestOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request a Connector</DialogTitle>
            <DialogDescription>
              Tell us which connector you need and we'll evaluate adding it.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5">
              <Label htmlFor="connector-name">Connector Name</Label>
              <Input
                id="connector-name"
                value={connectorName}
                onChange={(e) => setConnectorName(e.target.value)}
                placeholder="e.g. HubSpot, Notion, Stripe..."
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="connector-purpose">Purpose</Label>
              <Textarea
                id="connector-purpose"
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                placeholder="Describe how you plan to use this connector..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRequestOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleRequestSubmit} disabled={!connectorName.trim() || !purpose.trim() || submitting}>
              {submitting ? "Submitting…" : "Submit Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Connector Type Dialog */}
      <Dialog open={typeDialogOpen} onOpenChange={(open) => { setTypeDialogOpen(open); if (!open) cancelEdit(); }}>
        <DialogContent className={editingConnectorId || showAddForm ? "max-w-lg" : "max-w-md"}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {selectedConnectorType && <ConnectorLogo connector={selectedConnectorType} />}
              <div>
                <div>{selectedConnectorType?.name}</div>
                <p className="text-xs text-muted-foreground font-normal mt-0.5">{selectedConnectorType?.description}</p>
              </div>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 max-h-[65vh] overflow-y-auto">
            {/* Existing connectors list */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Your Connections</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { cancelEdit(); setShowAddForm(true); }}
                  disabled={showAddForm}
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Add New
                </Button>
              </div>

              {existingLoading ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground gap-2 text-sm">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading...
                </div>
              ) : existingConnectors.length === 0 && !showAddForm ? (
                <div className="flex flex-col items-center justify-center py-8 gap-2">
                  <Database className="h-8 w-8 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">No connections yet</p>
                  <Button
                    size="sm"
                    onClick={() => setShowAddForm(true)}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    Add your first connection
                  </Button>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {existingConnectors.map((conn) => (
                    <div
                      key={conn.id}
                      className="flex items-center gap-3 rounded-md border border-border px-3 py-2.5 group hover:border-primary/30 transition-colors"
                    >
                      <Database className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="text-sm font-medium truncate flex-1">{conn.name}</span>
                      {conn.connect_type === "remote" && (
                        <Badge variant="outline" className="text-[9px] px-1.5 py-0 shrink-0 border-blue-500/30 text-blue-400">
                          <Wifi className="h-2.5 w-2.5 mr-0.5" />
                          MCP
                        </Badge>
                      )}
                      <span className={`h-2 w-2 rounded-full shrink-0 ${conn.enabled ? "bg-emerald-400" : "bg-muted-foreground/30"}`} />
                      <span className="text-[11px] text-muted-foreground shrink-0">
                        {conn.enabled ? "Enabled" : "Disabled"}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => { setShowAddForm(false); startEditConnector(conn); }}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Edit existing connector form */}
            {editingConnectorId && (
              <div className="rounded-lg border border-border p-4 space-y-3 bg-accent/30">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Edit Connection</span>
                  <Button variant="ghost" size="sm" onClick={cancelEdit}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
                {selectedConnectorType?.connect_form ? (
                  <ConnectFormRenderer
                    fields={selectedConnectorType.connect_form}
                    values={editFormValues}
                    onChange={(name, value) => setEditFormValues((prev) => ({ ...prev, [name]: value }))}
                    isEdit
                  />
                ) : (
                  <div className="space-y-1.5">
                    <Label>Connection Name</Label>
                    <Input
                      placeholder={`My ${selectedConnectorType?.name || "Database"}`}
                      value={(editFormValues.name as string) ?? ""}
                      onChange={(e) => setEditFormValues((prev) => ({ ...prev, name: e.target.value }))}
                    />
                  </div>
                )}
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={cancelEdit} disabled={editSaving}>
                    Cancel
                  </Button>
                  <Button
                    className="flex-1"
                    onClick={handleSaveEdit}
                    disabled={!((editFormValues.name as string) || "").trim() || editSaving}
                  >
                    {editSaving ? "Saving…" : "Save Changes"}
                  </Button>
                </div>
              </div>
            )}

            {/* Add new connector form */}
            {showAddForm && (
              <div className="rounded-lg border border-border p-4 space-y-3 bg-accent/30">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">New Connection</span>
                  <Button variant="ghost" size="sm" onClick={() => { setShowAddForm(false); setFormValues({}); }}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>

                {/* Connect mode selector for connectors that support remote */}
                {selectedConnectorType?.allow_remote && (
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Connection Mode</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setConnectMode("sync")}
                        className={`flex items-center gap-2 rounded-lg border p-3 text-left text-sm transition-all ${
                          connectMode === "sync"
                            ? "border-primary bg-primary/10 text-foreground"
                            : "border-border hover:border-primary/30 text-muted-foreground"
                        }`}
                      >
                        <RefreshCw className="h-4 w-4 shrink-0" />
                        <div>
                          <div className="font-medium text-xs">Sync</div>
                          <div className="text-[10px] leading-tight mt-0.5">Sync data to local warehouse</div>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setConnectMode("remote")}
                        className={`flex items-center gap-2 rounded-lg border p-3 text-left text-sm transition-all ${
                          connectMode === "remote"
                            ? "border-primary bg-primary/10 text-foreground"
                            : "border-border hover:border-primary/30 text-muted-foreground"
                        }`}
                      >
                        <Wifi className="h-4 w-4 shrink-0" />
                        <div>
                          <div className="font-medium text-xs">Remote MCP</div>
                          <div className="text-[10px] leading-tight mt-0.5">Query directly via MCP</div>
                        </div>
                      </button>
                    </div>
                  </div>
                )}
                {selectedConnectorType?.connect_form ? (
                  <ConnectFormRenderer
                    fields={selectedConnectorType.connect_form}
                    values={formValues}
                    onChange={(name, value) => setFormValues((prev) => ({ ...prev, [name]: value }))}
                  />
                ) : (
                  <div className="space-y-1.5">
                    <Label>Connection Name</Label>
                    <Input
                      placeholder={`My ${selectedConnectorType?.name || "Database"}`}
                      value={(formValues.name as string) ?? ""}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, name: e.target.value }))}
                    />
                  </div>
                )}
                <Button
                  className="w-full"
                  onClick={handleAddConnector}
                  disabled={!(formValues.name as string)?.trim() || addingSaving}
                >
                  {addingSaving ? "Saving…" : "Add Connection (Disabled)"}
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AllConnectors;
